import { useCallback, useEffect, useState } from "react";
import { ITEM_KIND_ACCOUNTS, type CostCenterDto, type ItemKindAccount, type ItemKindDto, type ItemKindInput, type NamedInput, type PermissionKey, type TaxTypeDto, type TaxTypeInput } from "@delta/shared";
import { useLang } from "../../i18n";
import { useSession } from "../../lib/session";
import { api } from "../../lib/api";
import { errorText, fieldErrorsOf, referencesText } from "../../lib/errors";
import { Icon } from "../../components/Icon";
import { PageHead, Shell } from "../../components/Shell";
import { ListView, type Column } from "../../components/ListView";
import { Blocked, Confirm, Field, Modal } from "../../components/Modal";
import { useToast } from "../../components/Toast";
import { LockedButton, lockTag } from "../settings/common";
import { pct } from "../items/common";
import { AccName, AccountSelect, accText, useAccountOptions } from "./common";

// Settings › Cost centers, Settings › Tax settings and Items › Item Kinds, ported from the prototype
// (erp-settings.js "/cost-centers", "/settings/tax"; erp-items.js "/item-kinds"). Tax settings are configuration
// only: a name, a percentage and the account it is linked to. Nothing here calculates tax.

interface Keys { view: PermissionKey; add: PermissionKey; update: PermissionKey; del: PermissionKey }
const newButton = (can: (k: PermissionKey) => boolean, t: (k: string, v?: Record<string, string>) => string, k: PermissionKey, label: string, run: () => void) =>
  can(k) ? <button className="btn btn-primary" onClick={run}><Icon name="plus" />{label}</button> : <LockedButton label={label} title={t("noPermissionFor", { p: k })} />;

/** List state shared by the three screens: load with the name search, delete, refresh. */
function useList<R extends { id: string; nameEn: string; nameAr: string }>(path: string) {
  const { t, L } = useLang();
  const toast = useToast();
  const [rows, setRows] = useState<R[] | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [filters, setFilters] = useState<Record<string, string>>({});
  const load = useCallback(async () => {
    setFailed(null);
    try { setRows(await api<R[]>("GET", `${path}${filters.name ? "?q=" + encodeURIComponent(filters.name) : ""}`)); }
    catch (e) { setFailed(errorText(t, e)); }
  }, [path, filters.name, t]);
  useEffect(() => { void load(); }, [load]);
  async function remove(r: R) {
    try { await api("DELETE", `${path}/${r.id}`); toast(t("deletedOk", { x: L(r) })); }
    catch (e) { toast(errorText(t, e)); }
    await load();
  }
  return { rows, failed, filters, setFilters, load, remove };
}

const NameFields = ({ v, errs, set }: { v: NamedInput; errs: Record<string, string>; set(k: "nameAr" | "nameEn", x: string): void }) => {
  const { t } = useLang();
  return <>
    <Field label={t("nameAr")} req error={errs.nameAr} htmlFor="cf-ar"><input id="cf-ar" className="control" lang="ar" dir="rtl" value={v.nameAr} onChange={(e) => set("nameAr", e.target.value)} autoComplete="off" /></Field>
    <Field label={t("nameEn")} req error={errs.nameEn} htmlFor="cf-en"><input id="cf-en" className="control" lang="en" dir="ltr" value={v.nameEn} onChange={(e) => set("nameEn", e.target.value)} autoComplete="off" /></Field>
  </>;
};

/** Form state with required names and server field errors, shared by the three dialogs. */
function useForm<V extends NamedInput>(initial: () => V) {
  const { t } = useLang();
  const [v, setV] = useState<V>(initial);
  const [errs, setErrs] = useState<Record<string, string>>({});
  const [alert, setAlert] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const set = <K extends keyof V>(k: K, x: V[K]) => { setV((o) => ({ ...o, [k]: x })); setErrs((e) => ({ ...e, [k as string]: "" })); };
  async function submit(local: Record<string, string>, send: () => Promise<void>) {
    const e = { ...local };
    if (!v.nameAr.trim()) e.nameAr = t("requiredField");
    if (!v.nameEn.trim()) e.nameEn = t("requiredField");
    setErrs(e);
    if (Object.keys(e).length) { setAlert(t("fixRequired")); return; }
    setBusy(true); setAlert(null);
    try { await send(); } catch (err) { setErrs(fieldErrorsOf(t, err)); setAlert(errorText(t, err)); } finally { setBusy(false); }
  }
  return { v, set, errs, alert, busy, submit };
}

// ---------- cost centers ----------

const CC: Keys = { view: "costCenters", add: "costCenterAdd", update: "costCenterUpdate", del: "costCenterDelete" };

function CostCenterForm({ c, onClose, onSaved }: { c: CostCenterDto | null; onClose(): void; onSaved(x: CostCenterDto, created: boolean): void }) {
  const { t } = useLang();
  const f = useForm<NamedInput>(() => ({ nameAr: c?.nameAr ?? "", nameEn: c?.nameEn ?? "" }));
  const save = () => f.submit({}, async () => onSaved(c ? await api<CostCenterDto>("PUT", `/cost-centers/${c.id}`, f.v) : await api<CostCenterDto>("POST", "/cost-centers", f.v), !c));
  return <Modal title={t(c ? "editCostCenter" : "newCostCenter")} onClose={onClose} onSubmit={save} alert={f.alert} busy={f.busy}><NameFields v={f.v} errs={f.errs} set={f.set} /></Modal>;
}

export function CostCenters() {
  const { t, L } = useLang();
  const s = useSession();
  const toast = useToast();
  const l = useList<CostCenterDto>("/cost-centers");
  const [editing, setEditing] = useState<CostCenterDto | "new" | null>(null);
  const [deleting, setDeleting] = useState<CostCenterDto | null>(null);
  const columns: Column<CostCenterDto>[] = [
    { k: "ar", label: t("nameAr"), cell: (c) => <span lang="ar" className="cell-name">{c.nameAr}</span>, csv: (c) => c.nameAr },
    { k: "en", label: t("nameEn"), cell: (c) => <span lang="en">{c.nameEn}</span>, csv: (c) => c.nameEn },
    { k: "n", label: t("journalLines"), end: true, cell: (c) => <span className="num">{c.journalLinesCount}</span>, csv: (c) => c.journalLinesCount }
  ];
  return (
    <Shell navKey="/cost-centers">
      <PageHead crumbs={[[t("home"), "/home"], [t("settings")], [t("costCenters")]]} title={t("costCentersSettings")} />
      <ListView rows={l.rows} error={l.failed} columns={columns} file="cost-centers" testId="cost-centers-table" onRefresh={() => void l.load()}
        search={[{ k: "name", label: t("name"), type: "text" }]} filters={l.filters} onFilters={l.setFilters}
        quick={(c) => `${c.nameEn} ${c.nameAr}`} newButton={newButton(s.can, t, CC.add, t("newCostCenter"), () => setEditing("new"))}
        empty={{ icon: "chart", title: t("noCostCenters"), body: t("noCostCentersBody") }}
        actions={(c) => [
          { label: t("edit"), icon: "edit", run: () => setEditing(c), ...(s.can(CC.update) ? {} : lockTag(t)) },
          { label: "", sep: true },
          { label: t("delete"), icon: "trash", danger: true, run: () => setDeleting(c), ...(s.can(CC.del) ? {} : lockTag(t)) }
        ]} />
      {editing && <CostCenterForm c={editing === "new" ? null : editing} onClose={() => setEditing(null)}
        onSaved={(c, created) => { setEditing(null); toast(t(created ? "addedOk" : "updatedOk", { x: L(c) })); void l.load(); }} />}
      {deleting && <Confirm title={t("confirmDelete")} body={t("deleteWarn")} okLabel={t("yesSure")} onClose={() => setDeleting(null)} onOk={() => { const c = deleting; setDeleting(null); void l.remove(c); }} />}
    </Shell>
  );
}

// ---------- tax settings ----------

const TAX: Keys = { view: "taxTypes", add: "taxTypeAdd", update: "taxTypeUpdate", del: "taxTypeDelete" };
const PCT_RE = /^\d{1,3}(\.\d{1,2})?$/;

function TaxForm({ x, onClose, onSaved }: { x: TaxTypeDto | null; onClose(): void; onSaved(r: TaxTypeDto, created: boolean): void }) {
  const { t } = useLang();
  const { opts, failed } = useAccountOptions();
  const f = useForm<TaxTypeInput>(() => x ? { nameAr: x.nameAr, nameEn: x.nameEn, percentage: x.percentage, accountId: x.accountId } : { nameAr: "", nameEn: "", percentage: "", accountId: "" });
  const save = () => {
    const e: Record<string, string> = {};
    const p = String(f.v.percentage).trim();
    if (!f.v.accountId) e.accountId = t("requiredField");
    if (!p) e.percentage = t("requiredField");
    else if (!PCT_RE.test(p) || Number(p) > 100) e.percentage = /\.\d{3,}$/.test(p) ? t("twoDecimals") : t("percentRange");
    return f.submit(e, async () => { const body = { ...f.v, percentage: p }; onSaved(x ? await api<TaxTypeDto>("PUT", `/tax-types/${x.id}`, body) : await api<TaxTypeDto>("POST", "/tax-types", body), !x); });
  };
  return (
    <Modal title={t(x ? "editTax" : "newTax")} onClose={onClose} onSubmit={save} alert={f.alert} busy={f.busy}>
      <Field label={t("accountIndex")} req full error={f.errs.accountId} htmlFor="cf-acc"><AccountSelect id="cf-acc" value={f.v.accountId} onChange={(v) => f.set("accountId", v)} opts={opts} failed={failed} /></Field>
      <NameFields v={f.v} errs={f.errs} set={f.set} />
      <Field label={t("percentage")} req error={f.errs.percentage} hint={t("taxPercentHint")} htmlFor="cf-pct">
        <input id="cf-pct" className="control num" style={{ textAlign: "start" }} dir="ltr" inputMode="decimal" value={String(f.v.percentage)} onChange={(e) => f.set("percentage", e.target.value)} autoComplete="off" />
      </Field>
    </Modal>
  );
}

export function TaxTypes() {
  const { t, L } = useLang();
  const s = useSession();
  const toast = useToast();
  const l = useList<TaxTypeDto>("/tax-types");
  const [editing, setEditing] = useState<TaxTypeDto | "new" | null>(null);
  const [deleting, setDeleting] = useState<TaxTypeDto | null>(null);
  const [blocked, setBlocked] = useState<TaxTypeDto | null>(null);
  const columns: Column<TaxTypeDto>[] = [
    { k: "ar", label: t("nameAr"), cell: (x) => <span lang="ar" className="cell-name">{x.nameAr}</span>, csv: (x) => x.nameAr },
    { k: "en", label: t("nameEn"), cell: (x) => <span lang="en">{x.nameEn}</span>, csv: (x) => x.nameEn },
    { k: "rate", label: t("percentage"), end: true, cell: (x) => <span className="num">{pct(x.percentage)}</span>, csv: (x) => x.percentage },
    { k: "acc", label: t("accountIndex"), cell: (x) => <AccName a={x.account} />, csv: (x) => accText(L, x.account) }
  ];
  const inUse = (x: TaxTypeDto) => x.itemsCount + x.treasuriesCount > 0;
  return (
    <Shell navKey="/settings/tax">
      <PageHead crumbs={[[t("home"), "/home"], [t("settings")], [t("taxSettings")]]} title={t("taxSettings")} />
      <div className="dep-note" style={{ marginBottom: 14 }} data-testid="tax-config-note"><Icon name="info" /><span>{t("taxConfigOnly")}</span></div>
      <ListView rows={l.rows} error={l.failed} columns={columns} file="tax-types" testId="tax-types-table" onRefresh={() => void l.load()}
        search={[{ k: "name", label: t("name"), type: "text" }]} filters={l.filters} onFilters={l.setFilters}
        quick={(x) => `${x.nameEn} ${x.nameAr} ${x.percentage}`} newButton={newButton(s.can, t, TAX.add, t("newTax"), () => setEditing("new"))}
        empty={{ icon: "receipt", title: t("noResults"), body: t("noResultsBody") }}
        actions={(x) => [
          { label: t("edit"), icon: "edit", run: () => setEditing(x), ...(s.can(TAX.update) ? {} : lockTag(t)) },
          { label: "", sep: true },
          { label: t("delete"), icon: "trash", danger: true, run: () => (inUse(x) ? setBlocked(x) : setDeleting(x)), ...(s.can(TAX.del) ? {} : lockTag(t)) }
        ]} />
      {editing && <TaxForm x={editing === "new" ? null : editing} onClose={() => setEditing(null)}
        onSaved={(x, created) => { setEditing(null); toast(t(created ? "addedOk" : "updatedOk", { x: L(x) })); void l.load(); }} />}
      {deleting && <Confirm title={t("confirmDelete")} body={t("deleteWarn")} okLabel={t("yesSure")} onClose={() => setDeleting(null)} onOk={() => { const x = deleting; setDeleting(null); void l.remove(x); }} />}
      {blocked && <Blocked title={t("cantDelete")} body={`${t("taxInUse")} (${referencesText(t, { items: blocked.itemsCount, treasuries: blocked.treasuriesCount })})`} onClose={() => setBlocked(null)} />}
    </Shell>
  );
}

// ---------- item kinds ----------

const KIND: Keys = { view: "itemKinds", add: "itemKindAdd", update: "itemKindUpdate", del: "itemKindDelete" };
/** The six audited account fields and the prototype's labels (erp-items.js KACC). */
const KACC: [ItemKindAccount, string][] = ITEM_KIND_ACCOUNTS.map((f) => [f, f.replace(/Id$/, "")]);

function KindForm({ k, onClose, onSaved }: { k: ItemKindDto | null; onClose(): void; onSaved(x: ItemKindDto, created: boolean): void }) {
  const { t } = useLang();
  const { opts, failed } = useAccountOptions();
  // A new kind starts with no accounts chosen: Delta does not presume which accounts an organization uses.
  const f = useForm<ItemKindInput>(() => k
    ? { nameAr: k.nameAr, nameEn: k.nameEn, hasQty: k.hasQty, ...Object.fromEntries(ITEM_KIND_ACCOUNTS.map((a) => [a, k[a]])) } as ItemKindInput
    : { nameAr: "", nameEn: "", hasQty: true, ...Object.fromEntries(ITEM_KIND_ACCOUNTS.map((a) => [a, ""])) } as ItemKindInput);
  const save = () => f.submit(Object.fromEntries(ITEM_KIND_ACCOUNTS.filter((a) => !f.v[a]).map((a) => [a, t("requiredField")])),
    async () => onSaved(k ? await api<ItemKindDto>("PUT", `/item-kinds/${k.id}`, f.v) : await api<ItemKindDto>("POST", "/item-kinds", f.v), !k));
  return (
    <Modal title={t(k ? "editItemKind" : "newItemKind")} wide onClose={onClose} onSubmit={save} alert={f.alert} busy={f.busy}>
      <NameFields v={f.v} errs={f.errs} set={f.set} />
      {KACC.map(([a, label]) => (
        <Field key={a} label={t(label)} req error={f.errs[a]} htmlFor={`cf-${a}`}><AccountSelect id={`cf-${a}`} value={f.v[a]} onChange={(v) => f.set(a, v)} opts={opts} failed={failed} /></Field>
      ))}
      <div className="field full"><label className="check-row"><input type="checkbox" checked={f.v.hasQty} onChange={(e) => f.set("hasQty", e.target.checked)} data-testid="kind-hasqty" /> {t("hasQty")}</label></div>
    </Modal>
  );
}

export function ItemKinds() {
  const { t, L } = useLang();
  const s = useSession();
  const toast = useToast();
  const l = useList<ItemKindDto>("/item-kinds");
  const [editing, setEditing] = useState<ItemKindDto | "new" | null>(null);
  const [deleting, setDeleting] = useState<ItemKindDto | null>(null);
  const [blocked, setBlocked] = useState<ItemKindDto | null>(null);
  const columns: Column<ItemKindDto>[] = [
    { k: "ar", label: t("nameAr"), cell: (k) => <><b lang="ar">{k.nameAr}</b>{!k.hasQty && <> <span className="pill">{t("hasNoStock")}</span></>}</>, csv: (k) => k.nameAr },
    { k: "en", label: t("nameEn"), cell: (k) => <span lang="en">{k.nameEn}</span>, csv: (k) => k.nameEn },
    ...KACC.map(([a, label]): Column<ItemKindDto> => ({ k: a, label: t(label), cell: (k) => <AccName a={k.accounts[a]} />, csv: (k) => accText(L, k.accounts[a]) })),
    { k: "n", label: t("itemsCount"), end: true, cell: (k) => k.itemsCount, csv: (k) => k.itemsCount }
  ];
  return (
    <Shell navKey="/item-kinds">
      <PageHead crumbs={[[t("home"), "/home"], [t("items")], [t("itemKinds")]]} title={t("itemKinds")} />
      <ListView rows={l.rows} error={l.failed} columns={columns} file="item-kinds" testId="item-kinds-table" onRefresh={() => void l.load()}
        search={[{ k: "name", label: t("name"), type: "text" }]} filters={l.filters} onFilters={l.setFilters}
        quick={(k) => `${k.nameEn} ${k.nameAr}`} newButton={newButton(s.can, t, KIND.add, t("newItemKind"), () => setEditing("new"))}
        empty={{ icon: "box", title: t("noResults"), body: t("noResultsBody") }}
        actions={(k) => [
          { label: t("edit"), icon: "edit", run: () => setEditing(k), ...(s.can(KIND.update) ? {} : lockTag(t)) },
          { label: "", sep: true },
          { label: t("delete"), icon: "trash", danger: true, run: () => (k.itemsCount ? setBlocked(k) : setDeleting(k)), ...(s.can(KIND.del) ? {} : lockTag(t)) }
        ]} />
      {editing && <KindForm k={editing === "new" ? null : editing} onClose={() => setEditing(null)}
        onSaved={(k, created) => { setEditing(null); toast(t(created ? "addedOk" : "updatedOk", { x: L(k) })); void l.load(); }} />}
      {deleting && <Confirm title={t("confirmDelete")} body={t("deleteWarn")} okLabel={t("yesSure")} onClose={() => setDeleting(null)} onOk={() => { const k = deleting; setDeleting(null); void l.remove(k); }} />}
      {blocked && <Blocked title={t("cantDelete")} body={`${t("cantDeleteKind")} (${t("refCount_items", { n: blocked.itemsCount })})`} onClose={() => setBlocked(null)} />}
    </Shell>
  );
}
